import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { IntersectionStoryblok, IntersectionStatStoryblok } from '../../types/storyblok';

interface IntersectionProps {
  blok: IntersectionStoryblok;
}

interface StatItemProps {
  stat: IntersectionStatStoryblok;
}

/**
 * Individual stat item with value and label.
 * Value: large extrabold white text
 * Label: smaller semibold ice-blue text
 */
const StatItem: React.FC<StatItemProps> = ({ stat }) => {
  return (
    <div
      {...storyblokEditable(stat as unknown as SbBlokData)}
      className="flex flex-col items-center gap-2 text-center"
    >
      <span className="font-primary font-extrabold text-[40px] desktop:text-[54px] leading-normal text-neutral-white">
        {stat.value}
      </span>
      <span className="font-primary font-semibold text-[18px] leading-normal text-secondary-ice-blue">
        {stat.label}
      </span>
    </div>
  );
};

/**
 * Intersection component displays key statistics in a blue gradient banner.
 *
 * Desktop: Horizontal row of all stats with even spacing
 * Mobile: First stat (featured) on top, remaining stats in row below
 *
 * Blue gradient background: brand-blue to brand-deep
 */
export const Intersection: React.FC<IntersectionProps> = ({ blok }) => {
  const stats = blok.stats ?? [];
  const [firstStat, ...restStats] = stats;

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      className="bg-gradient-to-b from-brand-blue to-brand-deep px-4 py-10 desktop:px-20 desktop:py-16"
    >
      {/* Desktop: all stats in single row */}
      <div className="hidden desktop:flex items-center justify-center gap-20 max-w-[1440px] mx-auto">
        {stats.map((stat) => (
          <StatItem key={stat._uid} stat={stat} />
        ))}
      </div>

      {/* Mobile: first stat on top, rest in row below */}
      <div className="flex flex-col items-center gap-8 desktop:hidden">
        {firstStat && <StatItem stat={firstStat} />}
        {restStats.length > 0 && (
          <div className="flex items-center justify-center gap-8 w-full">
            {restStats.map((stat) => (
              <StatItem key={stat._uid} stat={stat} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
